// Ending a portal session without leaving protected data behind (SM-722).
//
// Three ways a session ends, one teardown:
//
//   expired     a 401 on ANY call (the cookie expired, was revoked, or the
//               user signed out in another tab)
//   signed-out  the user pressed Sign out
//   revoked     the user was broker staff in this tab and /api/broker/me
//               now refuses them (403 BROKER_SCOPE_DENIED / 404): every
//               membership was removed or suspended while they worked
//
// The teardown always clears the whole query cache (every figure the
// portal read lives there and nowhere else), removes the portal's CSRF
// token from storage, and navigates to /login with the reason. Node and
// currency live in the URL, not in storage, so there is nothing else to
// scrub. It runs at most once per session even when several in-flight
// calls fail with 401 together.
import { APIError } from "./api.ts";

export type SessionEndReason = "expired" | "signed-out" | "revoked";

export interface SessionDeps {
  /** queryClient.clear() */
  clearQueries(): void;
  /** setCSRF(null) */
  clearCsrf(): void;
  /** router.navigate({ to: "/login", search: { reason } }) */
  redirectToLogin(reason: SessionEndReason): void;
  /** Best-effort POST /api/auth/logout (revocation and sign-out). */
  serverLogout?(): Promise<void>;
}

export interface SessionController {
  /** Tear the session down (idempotent until reset()). */
  end(reason: SessionEndReason): Promise<void>;
  /** The api client's auth-failure hook: a 401 ends the session. */
  onApiError(err: unknown): void;
  /** A fresh sign-in re-arms the controller. */
  reset(): void;
  readonly ended: boolean;
}

export function createSessionController(deps: SessionDeps): SessionController {
  let ended = false;
  const controller: SessionController = {
    get ended() {
      return ended;
    },
    async end(reason) {
      if (ended) return;
      ended = true;
      // The cached figures go first: nothing protected may survive a
      // failed logout call.
      deps.clearQueries();
      // The server-side logout still needs the CSRF token (it is a POST),
      // so the token is dropped after it, whatever it answers.
      if (reason !== "expired" && deps.serverLogout) {
        try {
          await deps.serverLogout();
        } catch {
          // The cookie may already be gone; the local teardown is what
          // protects the data.
        }
      }
      deps.clearCsrf();
      deps.redirectToLogin(reason);
    },
    onApiError(err) {
      if (err instanceof APIError && err.status === 401) void controller.end("expired");
    },
    reset() {
      ended = false;
    },
  };
  return controller;
}

export type StaffMeFailure = "expired" | "not-staff" | "revoked" | "error";

/**
 * How the shell reads a failed GET /api/broker/me.
 *
 * 403 BROKER_SCOPE_DENIED means "signed in, holds no membership". On a
 * first bootstrap that is the "not broker staff" screen — a real answer,
 * not a login failure. In a tab that already held memberships it means
 * access was just revoked, and the session ends.
 */
export function classifyStaffMeFailure(err: unknown, hadStaffSession: boolean): StaffMeFailure {
  if (!(err instanceof APIError)) return "error";
  if (err.status === 401) return "expired";
  if (err.status === 403 || err.status === 404) {
    if (hadStaffSession) return "revoked";
    return err.status === 403 && err.code === "BROKER_SCOPE_DENIED" ? "not-staff" : "error";
  }
  return "error";
}
